import Loading from '@app/components/loading';
import { formatCurrency } from '@app/components/price/currency-formatter';
import {
  DeliveryDiscountType,
  HappyHourDiscountType,
  HappyHourMode,
  OnlineOrderBranchConfigurationItemFragment,
  PickupDiscountType,
} from '@app/graphql/types/graphql';
import { useTolgee } from '@tolgee/react';
import { lowerCase } from 'lodash';
import { useGetAvailableHappyHour } from '../logic/use-get-available-happy-banner';

export default function HappyHourBanner(props: {
  branchId: string;
  currency: string;
  configuration?: OnlineOrderBranchConfigurationItemFragment | null;
}) {
  const { data, loading, error } = useGetAvailableHappyHour(props.branchId);
  const { t } = useTolgee();

  if (loading) {
    return <Loading />;
  }

  if (error || !data?.getAvailableHappyHourByBranch) {
    return null;
  }

  const happyHours = data.getAvailableHappyHourByBranch;

  if (happyHours.length == 0) {
    return;
  }

  if (props.configuration?.happyHourMode == HappyHourMode.Basic) {
    let pickupDiscountText = '';
    let deliveryDiscountText = '';
    const minimumAmount = formatCurrency(props.configuration.minimumOrderAmountForDelivery, props.currency);

    switch (props.configuration.pickupDiscountType) {
      case PickupDiscountType.Percentage:
        pickupDiscountText = `${props.configuration.pickupDiscountAmount.toString()}%`;
        break;

      case PickupDiscountType.Amount:
        pickupDiscountText = formatCurrency(props.configuration.pickupDiscountAmount, props.currency).toString();
        break;
    }

    switch (props.configuration.deliveryDiscountType) {
      case DeliveryDiscountType.Percentage:
        deliveryDiscountText = `${props.configuration.deliveryDiscountAmount.toString()}%`;
        break;

      case DeliveryDiscountType.Amount:
        deliveryDiscountText = formatCurrency(props.configuration.deliveryDiscountAmount, props.currency).toString();
        break;
    }

    if (props.configuration.deliveryDiscountType == DeliveryDiscountType.None && props.configuration.pickupDiscountType == PickupDiscountType.None) {
      return null;
    }

    return (
      <div className="happy-hour-banner">
        <p className="happy-hour-banner__title">{t('happy-hour.banner.header', 'Happy hour for today')}</p>
        <ul className="happy-hour-banner__items">
          {props.configuration.pickupDiscountType !== PickupDiscountType.None && (
            <li className="happy-hour-banner__item">
              {t('happy-hour.banner.basic-des', 'Discount {discount} applicable for pickup order', {
                discount: pickupDiscountText,
              })}
            </li>
          )}

          {props.configuration.deliveryDiscountType !== DeliveryDiscountType.None && (
            <li className="happy-hour-banner__item">
              {t('happy-hour.banner.basic-des.delivery', 'Discount {discount} applicable for delivery order from {min}', {
                discount: deliveryDiscountText,
                min: minimumAmount,
              })}
            </li>
          )}
        </ul>
      </div>
    );
  }

  return (
    <div className="happy-hour-banner">
      <p className="happy-hour-banner__title">{t('happy-hour.banner.header', 'Happy hour for today')}</p>
      <ul className="happy-hour-banner__items">
        {happyHours.map((happyHour, index) => {
          const timeRule = happyHour.timeRule;
          if (timeRule.__typename !== 'MenuSectionRangeTimeRule') return null;

          let discountText = '';

          switch (happyHour.discountType) {
            case HappyHourDiscountType.Percentage:
              discountText = `${happyHour.discountAmount.toString()}%`;
              break;

            case HappyHourDiscountType.Amount:
              discountText = formatCurrency(happyHour.discountAmount, props.currency).toString();
              break;
          }

          const minimumOrderAmount = formatCurrency(happyHour.minimumOrderAmount, props.currency).toString();

          const hourRange = `${timeRule.start} - ${timeRule.end}`;

          const delivery = happyHour.deliveryType.map((item) => t(item, lowerCase(item))).join(', ');

          const dayOfWeeks = timeRule.daysOfWeek?.map((item) => t(item, lowerCase(item))).join(', ');

          return (
            <li key={index} className="happy-hour-banner__item">
              {t(
                'happy-hour.banner.des',
                'Discount {discount} applicable for orders from {min} within {hourRange} on {dayOfWeeks} for {delivery} orders',
                {
                  discount: discountText,
                  hourRange: hourRange,
                  min: minimumOrderAmount,
                  delivery: t(delivery, delivery),
                  dayOfWeeks: dayOfWeeks,
                }
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
